import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { FiDownload, FiTrash2, FiExternalLink, FiLoader, FiWatch } from 'react-icons/fi'
import { useStore } from '@/stores/useStore'
import { getModelsList, getTaskStatus } from '@/services/api'
import Model3DViewer from '@/components/Model3DViewer'
import LoginModal from '@/components/LoginModal'

export default function Dashboard() {
  const { t } = useTranslation()
  const { isAuthenticated, history, removeHistory, setCurrentModel } = useStore()
  
  const [serverModels, setServerModels] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedModel, setSelectedModel] = useState<any>(null)
  const [, setRefreshing] = useState(false)
  const [loginModalOpen, setLoginModalOpen] = useState(false)

  // Fetch models from server if authenticated
  useEffect(() => {
    const fetchModels = async () => {
      if (!isAuthenticated) {
        setLoading(false)
        return
      }

      try {
        const { models } = await getModelsList()
        if (models) {
          const formattedModels = models.map((m: any) => ({
            id: m.id,
            taskId: m.task_id,
            modelUrl: m.model_url,
            thumbnailUrl: m.thumbnail_url,
            timestamp: m.created_at,
            mode: 'image',
            syncedToNft: true,
            nftId: m.id,
            name: m.model_name,
          }))
          setServerModels(formattedModels)
        }
      } catch (err) {
        console.error('Failed to fetch models:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchModels()
  }, [isAuthenticated])

  // Combine server models with local history
  const allModels = [...serverModels]
  history.forEach((item) => {
    if (!allModels.find((m) => m.taskId === item.taskId || m.id === item.id)) {
      allModels.push(item)
    }
  })
  allModels.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())

  const handleSelectModel = async (model: any) => {
    setSelectedModel(model)
    setCurrentModel(model.modelUrl)
  }

  // @ts-ignore - Reserved for future use
  const _handleRefreshModel = async (model: any) => {
    if (!model.taskId) return

    setRefreshing(true)
    try {
      const status = await getTaskStatus(model.taskId, model.mode || 'image')
      if (status.status === 'SUCCEEDED') {
        const modelUrl = status.model_urls?.glb || status.model_url
        if (modelUrl) {
          setCurrentModel(modelUrl)
          setSelectedModel({ ...model, modelUrl })
        }
      }
    } catch (err) {
      console.error('Failed to refresh model:', err)
    } finally {
      setRefreshing(false)
    }
  }

  const handleDelete = (id: string) => {
    if (confirm('确定要删除这个模型吗？')) {
      removeHistory(id)
      if (selectedModel?.id === id) {
        setSelectedModel(null)
        setCurrentModel(null)
      }
    }
  }

  const handleListNFT = (model: any) => {
    if (!isAuthenticated) {
      setLoginModalOpen(true)
      return
    }
    ;(window as any).openNFTModal?.(model)
  }

  // Show login prompt if not authenticated and no local history
  if (!isAuthenticated && history.length === 0) {
    return (
      <div className="min-h-screen py-8">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-serif font-bold text-white mb-8">{t('dashboard.title')}</h1>
          
          <div className="bg-luxury-navy-900/50 backdrop-blur-sm rounded-lg border border-luxury-gold-800/30 p-12 text-center">
            <div className="w-24 h-24 bg-luxury-gold-800/20 rounded-full mx-auto mb-6 flex items-center justify-center">
              <span className="text-4xl">🔐</span>
            </div>
            <h2 className="text-2xl font-semibold text-white mb-4">{t('dashboard.loginRequired')}</h2>
            <p className="text-gray-400 mb-8 max-w-md mx-auto">
              登录后可以查看和管理您保存的3D模型，并将它们上架到NFT市场。
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setLoginModalOpen(true)}
                className="px-8 py-3 bg-gradient-to-r from-luxury-gold-600 to-luxury-gold-500 text-luxury-navy-950 font-semibold rounded-lg hover:from-luxury-gold-500 hover:to-luxury-gold-400 transition-colors"
              >
                登录
              </button>
              <Link
                to="/create"
                className="px-8 py-3 border border-luxury-gold-800/50 text-gray-300 rounded-lg hover:border-luxury-gold-500/50 hover:text-white transition-colors"
              >
                先去创建模型
              </Link>
            </div>
          </div>
        </div>
        
        <LoginModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
      </div>
    )
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <h1 className="text-3xl font-serif font-bold text-white mb-8">{t('dashboard.title')}</h1>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <FiLoader className="w-8 h-8 text-luxury-gold-500 animate-spin" />
          </div>
        ) : allModels.length === 0 ? (
          <div className="bg-luxury-navy-900/50 backdrop-blur-sm rounded-lg border border-luxury-gold-800/30 p-12 text-center">
            <div className="w-24 h-24 bg-luxury-gold-800/20 rounded-full mx-auto mb-6 flex items-center justify-center">
              <span className="text-4xl">📦</span>
            </div>
            <h2 className="text-2xl font-semibold text-white mb-4">{t('dashboard.empty')}</h2>
            <Link
              to="/create"
              className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-luxury-gold-600 to-luxury-gold-500 text-luxury-navy-950 font-semibold rounded-lg hover:from-luxury-gold-500 hover:to-luxury-gold-400 transition-colors"
            >
              创建3D模型
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Model List */}
            <div className="lg:col-span-1 space-y-4">
              {allModels.map((model) => (
                <div
                  key={model.id}
                  onClick={() => handleSelectModel(model)}
                  className={`bg-luxury-navy-900/50 backdrop-blur-sm rounded-lg border p-4 cursor-pointer transition-colors ${
                    selectedModel?.id === model.id
                      ? 'border-luxury-gold-500'
                      : 'border-luxury-gold-800/30 hover:border-luxury-gold-500/50'
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    {model.thumbnailUrl ? (
                      <img
                        src={model.thumbnailUrl}
                        alt={model.name}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-luxury-navy-800 rounded-lg flex items-center justify-center">
                        <span className="text-2xl">📦</span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-white font-medium truncate">
                        {model.name || model.brand || 'Model'}
                      </h3>
                      <p className="text-gray-500 text-sm">
                        {new Date(model.timestamp).toLocaleDateString()}
                      </p>
                      {model.syncedToNft && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-green-900/50 text-green-400 text-xs rounded">
                          已上架
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Model Preview */}
            <div className="lg:col-span-2">
              <div className="bg-luxury-navy-900/50 backdrop-blur-sm rounded-lg border border-luxury-gold-800/30 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-white">
                    {selectedModel?.name || '选择一个模型'}
                  </h2>
                  {selectedModel && (
                    <div className="flex space-x-2">
                      {/* 一键试戴按钮 */}
                      {selectedModel.modelUrl && (
                        <a
                          href={`/Mobile_AR/wrist-test.html?model=${encodeURIComponent(selectedModel.modelUrl)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 bg-purple-600 rounded-lg text-white hover:bg-purple-500 transition-colors"
                          title="一键试戴"
                        >
                          <FiWatch className="w-5 h-5" />
                        </a>
                      )}
                      {selectedModel.modelUrl && (
                        <a
                          href={selectedModel.modelUrl}
                          download
                          className="p-2 bg-luxury-navy-800 rounded-lg text-gray-400 hover:text-white transition-colors"
                          title="下载"
                        >
                          <FiDownload className="w-5 h-5" />
                        </a>
                      )}
                      {!selectedModel.syncedToNft && (
                        <button
                          onClick={() => handleListNFT(selectedModel)}
                          className="p-2 bg-luxury-navy-800 rounded-lg text-gray-400 hover:text-luxury-gold-500 transition-colors"
                          title="上架NFT"
                        >
                          <FiExternalLink className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(selectedModel.id)}
                        className="p-2 bg-luxury-navy-800 rounded-lg text-gray-400 hover:text-red-500 transition-colors"
                        title="删除"
                      >
                        <FiTrash2 className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
                <div className="h-[500px] rounded-lg overflow-hidden bg-luxury-navy-800">
                  <Model3DViewer modelUrl={selectedModel?.modelUrl || null} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <LoginModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </div>
  )
}
